import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router';
import Delta from 'quill-delta';
import * as _ from 'underscore';

import * as graphql from 'generated/graphql';
import useErrorBoundary from 'packages/kakapo-ui-application/hooks/useErrrorBoundary';
import { Recipient } from 'packages/kakapo-ui-services/models/Recipient';

import useLocalize from '../../../hooks/useLocalize';

import FeedbackRequestModalLoading from './FeedbackRequestModalLoading';
import FeedbackRequestModalMethod from './FeedbackRequestModalMethod';
import FeedbackRequestModalQuestions from './FeedbackRequestModalQuestions';
import FeedbackRequestModalRecipients from './FeedbackRequestModalRecipients';
import FeedbackRequestThankYou from './FeedbackRequestThankYou';
import { Popover } from '@treams/treams-ui';

type FeedbackRequestMethod = 'custom' | 'template' | 'competencies' | undefined;

function methodFromSettings(
	momentSettings: graphql.MomentFeedback | undefined,
): FeedbackRequestMethod {
	if (!momentSettings) {
		return undefined;
	}

	if (momentSettings.feedbackTemplate) {
		return 'template';
	}

	return undefined;
}

function stepFromSettings(momentSettings: graphql.MomentFeedback | undefined): number {
	const method = methodFromSettings(momentSettings);

	if (method === 'template') {
		return 1;
	}

	return 0;
}

function FeedbackRequestModalContents({
	method: defaultMethod,
	userID,
	returnURL,
	refresh,
	momentID,
	momentSettings,
	momentTitle,
}: {
	method?: FeedbackRequestMethod;
	userID: string;
	returnURL: string;
	refresh: () => void;
	momentID?: string;
	momentSettings?: graphql.MomentFeedback;
	momentTitle?: string;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const [error, attempt] = useErrorBoundary();

	const [step, setStep] = useState(
		defaultMethod !== undefined ? 1 : stepFromSettings(momentSettings),
	);
	const [method, setMethod] = useState<FeedbackRequestMethod>(
		defaultMethod ?? methodFromSettings(momentSettings),
	);
	const [title, setTitle] = useState<string | undefined>(momentTitle);
	const [instruction, setInstruction] = useState<Delta | undefined>(
		momentSettings?.feedbackTemplate?.instruction
			? localize(momentSettings?.feedbackTemplate?.instruction)?.text
			: undefined,
	);
	const [template, setTemplate] = useState<graphql.FeedbackTemplate | undefined>(
		momentSettings?.feedbackTemplate ?? undefined,
	);
	const [questions, setQuestions] = useState<
		(graphql.FeedbackRequestQuestionInput & { id: string })[]
	>(
		momentSettings?.feedbackTemplate
			? momentSettings?.feedbackTemplate?.questions?.map(({ question, instruction }) => ({
					id: Math.random().toString(),
					type: question.type,
					text: localize(question.text).text,
					description: localize(instruction ?? [])?.text,
			  }))
			: [],
	);

	const [goals, setGoals] = useState<{ goalID: string; text?: string; order: number }[]>([]);
	const [competencies, setCompetencies] = useState<{ competencyID: string; text?: string }[]>([]);

	const [recipients, setRecipients] = useState<Recipient[]>([]);

	const [sent, setSent] = useState(false);

	const [create, { loading: loadingCreate }] = graphql.useCreateFeedbackRequestMutation();

	const [requestInternal, { loading: loadingInternal }] =
		graphql.useRequestInternalFeedbackMutation();

	const [requestExternal, { loading: loadingExternal }] =
		graphql.useRequestExternalFeedbackMutation();

	const loading = loadingCreate || loadingInternal || loadingExternal;

	if (loading) {
		return <FeedbackRequestModalLoading returnURL={returnURL} />;
	}

	if (sent) {
		return <FeedbackRequestThankYou numberOfRecipients={recipients.length} returnURL={returnURL} />;
	}

	if (step === 0) {
		return (
			<FeedbackRequestModalMethod
				returnURL={returnURL}
				onChange={(method) => {
					setMethod(method);
					setStep(step + 1);

					if (method === 'custom') {
						setQuestions([
							{
								id: Math.random().toString(),
								description: { ops: [] },
								text: '',
								type: graphql.FeedbackQuestionType.Text,
							},
						]);
					}
				}}
			/>
		);
	}

	if (step === 1) {
		return (
			<FeedbackRequestModalQuestions
				method={method ?? 'custom'}
				userID={userID}
				needsTemplate={method === 'template'}
				instruction={instruction}
				template={template}
				title={title}
				questions={questions}
				goals={goals}
				competencies={competencies}
				returnURL={returnURL}
				onChange={(questions, goals, competencies, title, instruction, template) => {
					setQuestions(questions);
					setGoals(goals);
					setCompetencies(competencies);
					setTitle(title);
					setInstruction(instruction);
					setTemplate(template);
				}}
				onBack={() => {
					setQuestions([]);
					setInstruction(undefined);
					setTemplate(undefined);
					setStep(step - 1);
				}}
				minimumQuestions={momentSettings?.minimumQuestions ?? undefined}
				onDone={() => setStep(step + 1)}
			/>
		);
	}

	if (step === 2) {
		return (
			<FeedbackRequestModalRecipients
				recipients={recipients}
				returnURL={returnURL}
				error={error}
				onBack={() => setStep(step - 1)}
				minimumRecipients={momentSettings?.minimumRecipients ?? undefined}
				maximumRecipients={momentSettings?.maximumRecipients ?? undefined}
				feedbackTemplateID={momentSettings?.feedbackTemplate?.id}
				onChange={setRecipients}
				onDone={attempt(async (dueDate, internals, externals) => {
					const filledQuestions = questions.filter((question) => question.text !== '');

					const response = await create({
						variables: {
							input: {
								userID,
								questions: filledQuestions
									.map((question) => {
										return _.omit(
											{
												...question,
												description: question.description ?? { ops: [] },
											},
											'id',
										);
									})
									.concat(
										goals.map(({ goalID, text }) => {
											return {
												text: text ?? t('feedback.composer.goals.progressQuestion'),
												description: { ops: [] },
												type: graphql.FeedbackQuestionType.Text,
												goalID,
											};
										}),
									)
									.concat(
										method === 'competencies'
											? competencies.map(({ competencyID, text }) => {
													return {
														text: text ?? t('feedback.composer.competencies.question'),
														description: { ops: [] },
														type: graphql.FeedbackQuestionType.Text,
														competencyID,
													};
											  })
											: [],
									),
								dueDate,
								title: title || (template ? localize(template.title).text : ''),
								instruction: instruction ?? localize(template?.instruction ?? [])?.text,
								momentID,
							},
						},
					});

					const requestID = response.data!.createFeedbackRequest.feedbackRequest.id;

					for (const userID of internals) {
						await requestInternal({ variables: { input: { requestID, userID } } });
					}

					for (const { email, firstName, lastName } of externals) {
						await requestExternal({
							variables: {
								input: {
									requestID,
									email,
									firstName,
									lastName,
								},
							},
						});
					}

					setSent(true);

					refresh();
				})}
			/>
		);
	}

	return null;
}

export default function FeedbackRequestModal({
	method,
	userID,
	refresh,
	returnURL,
}: {
	method?: FeedbackRequestMethod;
	userID: string;
	returnURL: string;
	refresh: () => void;
}) {
	return (
		<Popover visible center>
			<FeedbackRequestModalContents
				method={method}
				userID={userID}
				returnURL={returnURL}
				refresh={refresh}
			/>
		</Popover>
	);
}

export function FeedbackRequestModalMoment({
	method,
	userID,
	refresh,
	returnURL,
}: {
	method?: FeedbackRequestMethod;
	userID: string;
	returnURL: string;
	refresh: () => void;
}) {
	const { localize } = useLocalize();
	const { params } = useRouteMatch<{ momentID: string }>();

	const { data: momentInstanceData } = graphql.useMomentForFeedbackQuery({
		variables: { id: params.momentID },
	});

	return (
		<Popover visible center>
			{momentInstanceData ? (
				<FeedbackRequestModalContents
					method={method}
					userID={userID}
					returnURL={returnURL}
					refresh={refresh}
					momentID={params.momentID}
					momentTitle={localize(momentInstanceData.momentInstance.momentTemplate.title).text}
					momentSettings={
						momentInstanceData.momentInstance.momentTemplate.feedback as graphql.MomentFeedback
					}
				/>
			) : (
				<FeedbackRequestModalLoading returnURL={returnURL} />
			)}
		</Popover>
	);
}
